/* eslint-disable ember/no-incorrect-calls-with-inline-anonymous-functions, ember/no-computed-properties-in-native-classes */
/* eslint-disable ember/no-runloop */
import { action, computed } from '@ember/object';
import { begin, end, scheduleOnce } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismMessage } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import getChartColor from 'frontend-utils/utils/hotel-chart-color';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import HotelStrategyTooltipsTooltipChartDetailView from 'lumen/components/hotel/strategy/tooltips/tooltip-chart-detail-view';
import { RESIZE_EVENTS } from 'lumen/services/resize';
import { SUBTYPE_VALUES_KEY_PATTERN } from 'lumen/utils/constants/rate-strategy-constants';
import prepareTooltipData from 'lumen/utils/strategy/tooltip-data-format-helpers';

interface HotelStrategyChartsChartRoomtypePatternSignature {
  Args: {
    data: unknown;
    isDetailView: unknown;
    onUpdateChartReference: unknown;
  };
}

export default class HotelStrategyChartsChartRoomtypePattern extends Component<HotelStrategyChartsChartRoomtypePatternSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare resize: Services['resize'];
  @service declare rsSelections: Services['rs-selections'];

  // @ts-expect-error: Incorrect type
  @tracked hoveredSubtype;
  @tracked width = 600;

  isDetailView = true;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    const seriesMap = {};

    const ownColor = this.args.isDetailView
      ? getChartColor(
          // @ts-expect-error: Incorrect type
          this.ownHotelInfo.id,
          this.hotelAndMarketService.primaryAndSecondaryCompetitors,
        )
      : 'var(--prism-color-chart-sequential-info-08)';
    // First add the own hotel series
    const ownHotelSeries = {
      // @ts-expect-error: Incorrect type
      name: this.ownHotelInfo.name,
      // @ts-expect-error: Incorrect type
      hotelinfo: this.ownHotelInfo.id,
      data: [],
      type: 'scatter',
      zIndex: 2,
      marker: {
        symbol: 'rectangle',
        fillColor: ownColor,
        lineColor: ownColor,
        states: {
          hover: {
            enabled: false,
          },
        },
      },
      color: 'var(--prism-color-chart-sequential-info-08)',
    };

    const series = [ownHotelSeries];

    // @ts-expect-error: Incorrect type
    seriesMap[this.ownHotelInfo.id] = ownHotelSeries;

    // Then all of the competitors (activecompset will be the primary compset)
    this.rsSelections.filteredCompetitors.forEach((competitor) => {
      const color = this.args.isDetailView
        ? getChartColor(
            // @ts-expect-error: Incorrect type
            competitor.id,
            this.hotelAndMarketService.primaryAndSecondaryCompetitors,
          )
        : 'var(--prism-color-chart-accent-primary-figure-subdued)';
      const competitorSeries = {
        name: competitor.name,
        hotelinfo: competitor.id,
        data: [],
        type: 'scatter',
        color: 'var(--prism-color-chart-accent-primary-figure-subdued)',
        zIndex: 1,
        marker: {
          symbol: 'rectangle',
          fillColor: color,
          lineColor: color,
          states: {
            hover: {
              enabled: false,
            },
          },
        },
      };

      series.push(competitorSeries);
      // @ts-expect-error: Incorrect type
      seriesMap[competitor.id] = competitorSeries;
    });

    // Here we will keep the values for the columnRange
    // @ts-expect-error: Incorrect type
    const minMaxPerTypes = [];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.sortedRoomtypes.forEach((type) => {
      // Dummy values for the initial min and maxes
      let max = 0;
      let min = Infinity;

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      type.filteredPatterns.forEach(({ rate, hotelinfo, hasNoData }) => {
        // First check if what the API returns is actually in the compset/own hotel
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (seriesMap[hotelinfo.id]) {
          if (!hasNoData) {
            // The rate is for the scatter charts
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            seriesMap[hotelinfo.id].data.push(rate);

            if (rate < min) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              min = rate;
            }

            if (rate > max) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              max = rate;
            }
          } else {
            // if the pattern is missing, insert a dummy value
            // all series should have an equal amount of values, or the actual values will not be placed in the correct category
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            seriesMap[hotelinfo.id].data.push(null);
          }
        }
      });

      minMaxPerTypes.push([min, max]);
    });

    if (!this.args.isDetailView) {
      series.push({
        type: 'columnrange',
        name: 'range',
        enableMouseTracking: false,
        borderWidth: 0,
        color: 'var(--prism-color-chart-accent-primary-figure-default)',
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        data: minMaxPerTypes,
        states: {
          hover: {
            enabled: false,
          },
        },
      });
    }

    return series;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    // this is just defined by experimenting with what looks good
    const maxRectangleWidth = this.width / 3;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const numberOfRoomtypes = this.sortedRoomtypes.length;

    // The width of the bars, which will display the rates
    const rectangleWidth = maxRectangleWidth / numberOfRoomtypes;

    return {
      chart: {
        type: 'columnrange',
      },
      xAxis: {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        categories: that.sortedRoomtypes.map(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
          (type) => type.formattedLabel[SUBTYPE_VALUES_KEY_PATTERN],
        ),
      },
      yAxis: {
        min: 0,
        title: {
          text: '',
        },
        labels: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter() {
            const label = that.hotelAndMarketService.formatRate(
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              this.value,
              true,
              false,
            );

            return label === null ? label : `${label}`;
          },
        },
      },
      tooltip: {
        hideDelay: 0.2,
        padding: 0,
        borderWidth: 0,
        shadow: false,
        shared: true,
        style: {
          width: 600, // Will not be followed 100%, but at least pushes open the Highcharts generated tooltip elements as much as possible (first span under .highcharts-tooltip)
        },
        outside: true, // allows the tooltip to render outside the chart's SVG element box (required because else the chart tooltip can get cut off)
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          begin();
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          that.hoveredSubtype = this.x.toString();
          end();

          // @ts-expect-error: Incorrect type
          return document.getElementById(
            'dummy-tooltip-for-chart-roomtype-pattern',
          ).innerHTML;
        },
        useHTML: true,
      },
      legend: {
        enabled: this.args.isDetailView,
        itemStyle: {
          color: 'var(--prism-color-text-neutral-subdued)',
        },
        itemHoverStyle: {
          color: 'var(--prism-color-interactive-text-secondary-hover)',
        },
      },
      plotOptions: {
        series: {
          stickyTracking: true,
          marker: {
            radius: rectangleWidth,
          },
          pointWidth: rectangleWidth * 2 + 4, // For some reason the svg is a little bit bigger than the column, this fixes that (oh and * 2 since the scatter one is a radius)
        },
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dataForHoveredSubtypeTooltip() {
    let tooltipData = [];

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.hoveredSubtypeData.length) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      tooltipData = prepareTooltipData(
        this.hotelAndMarketService,
        this.intl,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.hoveredSubtypeData[0],
        false,
      );
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return tooltipData;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hoveredSubtypeData() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.sortedRoomtypes.filter(
      // @ts-expect-error: Incorrect type
      (type) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        type.formattedLabel[SUBTYPE_VALUES_KEY_PATTERN] === this.hoveredSubtype,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownHotelInfo() {
    return this.hotelAndMarketService.hotel?.hotelinfo;
  }

  @computed('args.data.filteredSubtypes')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedRoomtypes() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.data?.filteredSubtypes;
  }

  constructor() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);
    this.resize.registerResizeEvent(
      RESIZE_EVENTS.MODAL,
      this.resetChartWidth.bind(this),
    );
  }

  willDestroy(): void {
    super.willDestroy();

    this.resize.unregisterResizeEvent(RESIZE_EVENTS.MODAL);
  }

  @action
  resetChartWidth(): void {
    scheduleOnce('afterRender', this, () => {
      // @ts-expect-error: Incorrect type
      this.width = document.getElementById(
        'chart-roomtype-pattern',
      )?.offsetWidth;
    });
  }

  <template>
    <div id="chart-roomtype-pattern">
      {{! @glint-expect-error: Incorrect type }}
      {{#if @data.isMissing}}
        <div
          class="rate-strategy-empty-state-container flex items-center justify-center"
        >
          <PrismMessage
            @description={{t "stategy.empty_state.partial.message"}}
            @illustration="100x100_progress"
            @title={{t "stategy.empty_state.partial.message"}}
          />
        </div>
      {{else}}
        <GeneralChartsOtaChart
          {{! @glint-expect-error: Incorrect type }}
          @chartOptions={{this.chartOptions}}
          @model={{this.chartData}}
          {{! @glint-expect-error: Incorrect type }}
          @onUpdateChartReference={{@onUpdateChartReference}}
        />

        <div class="hidden" id="dummy-tooltip-for-chart-roomtype-pattern">
          <HotelStrategyTooltipsTooltipChartDetailView
            @dataForTooltip={{this.dataForHoveredSubtypeTooltip}}
            @ratesColumnHeaderTranslation={{t "rates.lowest_rate"}}
            @tooltipLabel={{this.hoveredSubtype}}
          />
        </div>
      {{/if}}
    </div>
  </template>
}
